import type { HttpClient } from '../http/types';
import type {
  BackendWorkItem,
  BackendWorkItemHistoryEvent,
  BackendWorkItemComment,
  BackendWorkItemSection,
  BackendWorkItemThread,
  BackendWorkItemType,
  CreateWorkItemPayload,
  MoveWorkItemBoardPayload,
  MoveWorkItemOrganizationPayload,
  ProjectView,
  UpdateWorkItemPayload,
  WorkBoard,
  WorkBoardParams,
} from '../types/work-item';
import { ENDPOINTS } from './endpoints';

export function createWorkItemsApi(http: HttpClient) {
  const pendingThreads = new Map<string, Promise<BackendWorkItemThread>>();

  return {
    list: (projectId: string) => http.get<BackendWorkItem[]>(ENDPOINTS.workItems.root(projectId)),

    getById: (projectId: string, workItemId: string) =>
      http.get<BackendWorkItem>(ENDPOINTS.workItems.byId(projectId, workItemId)),

    history: (projectId: string, workItemId: string) =>
      http.get<BackendWorkItemHistoryEvent[]>(ENDPOINTS.workItems.history(projectId, workItemId)),

    create: (projectId: string, payload: CreateWorkItemPayload) =>
      http.post<BackendWorkItem>(ENDPOINTS.workItems.root(projectId), payload),

    update: (projectId: string, workItemId: string, payload: UpdateWorkItemPayload) =>
      http.patch<void>(ENDPOINTS.workItems.byId(projectId, workItemId), payload),

    /** Move o item na hierarquia (pai + ordem entre irmãos). */
    organization: (
      projectId: string,
      workItemId: string,
      payload: MoveWorkItemOrganizationPayload,
    ) => http.patch<void>(ENDPOINTS.workItems.organization(projectId, workItemId), payload),

    /** Move o card no quadro (coluna + ordem dentro dela). */
    board: (projectId: string, workItemId: string, payload: MoveWorkItemBoardPayload) =>
      http.patch<void>(ENDPOINTS.workItems.board(projectId, workItemId), payload),

    /** Move o item para uma lista nomeada de tarefas. */
    taskList: (projectId: string, workItemId: string, taskListId: string) =>
      http.patch<void>(ENDPOINTS.workItems.taskList(projectId, workItemId), { taskListId }),

    ensureThread: (projectId: string, workItemId: string) => {
      const key = `${projectId}:${workItemId}`;
      const pending = pendingThreads.get(key);
      if (pending) return pending;
      const request = http
        .post<BackendWorkItemThread>(ENDPOINTS.workItems.thread(projectId, workItemId))
        .finally(() => {
          pendingThreads.delete(key);
        });
      pendingThreads.set(key, request);
      return request;
    },

    getThread: (projectId: string, threadId: string) =>
      http.get<BackendWorkItemThread>(ENDPOINTS.threads.byId(projectId, threadId)),

    createComment: (projectId: string, threadId: string, content: string) =>
      http.post<BackendWorkItemComment>(ENDPOINTS.threads.comments(projectId, threadId), {
        content,
      }),

    updateComment: (projectId: string, commentId: string, content: string) =>
      http.patch<BackendWorkItemComment>(ENDPOINTS.projects.commentById(projectId, commentId), {
        content,
      }),

    deleteComment: (projectId: string, commentId: string) =>
      http.delete<void>(ENDPOINTS.projects.commentById(projectId, commentId)),
  };
}

export function createWorkBoardApi(http: HttpClient) {
  return {
    get: (projectId: string, params?: WorkBoardParams) =>
      http.get<WorkBoard>(ENDPOINTS.workBoard.root(projectId), {
        query: params
          ? {
              ...params,
              assigneeId: params.assigneeId ? [...params.assigneeId] : undefined,
              sectionId: params.sectionId ? [...params.sectionId] : undefined,
            }
          : undefined,
      }),
  };
}

export function createProjectViewApi(http: HttpClient) {
  return {
    get: (projectId: string) => http.get<ProjectView>(ENDPOINTS.projectView.root(projectId)),
  };
}

export function createWorkItemSectionsApi(http: HttpClient) {
  return {
    list: (projectId: string) =>
      http.get<BackendWorkItemSection[]>(ENDPOINTS.workItemSections.root(projectId)),

    create: (projectId: string, name: string) =>
      http.post<BackendWorkItemSection>(ENDPOINTS.workItemSections.root(projectId), { name }),
  };
}

export function createWorkItemTypesApi(http: HttpClient) {
  return {
    list: (projectId: string) =>
      http.get<BackendWorkItemType[]>(ENDPOINTS.workItemTypes.root(projectId)),
  };
}

export type WorkItemsApi = ReturnType<typeof createWorkItemsApi>;
export type WorkBoardApi = ReturnType<typeof createWorkBoardApi>;
export type ProjectViewApi = ReturnType<typeof createProjectViewApi>;
export type WorkItemSectionsApi = ReturnType<typeof createWorkItemSectionsApi>;
export type WorkItemTypesApi = ReturnType<typeof createWorkItemTypesApi>;

export interface BackendTaskList {
  id: string;
  projectId: string;
  milestoneId: string | null;
  name: string;
  sequence: number;
  createdAt: string;
  updatedAt: string;
}

export function createTaskListsApi(http: HttpClient) {
  return {
    list: (projectId: string) => http.get<BackendTaskList[]>(ENDPOINTS.taskLists.root(projectId)),
    create: (projectId: string, name: string) =>
      http.post<BackendTaskList>(ENDPOINTS.taskLists.root(projectId), { name }),
    update: (projectId: string, taskListId: string, name: string) =>
      http.patch<BackendTaskList>(ENDPOINTS.taskLists.byId(projectId, taskListId), { name }),
    delete: (projectId: string, taskListId: string) =>
      http.delete<void>(ENDPOINTS.taskLists.byId(projectId, taskListId), {
        body: { confirmation: 'DELETAR' },
      }),
  };
}

export type TaskListsApi = ReturnType<typeof createTaskListsApi>;
